import { Dashboard } from "@/components/dashboard/Dashboard/Dashboard";
import { VStack } from "@/components/layout/VStack";
import { useEffect, useState } from "react";
import { useSignUp } from "../hooks/signUp/useSignUp";
import { Skeleton } from "@/components/ui/Skeleton/Skeleton";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/Empty/Empty";
import { CalendarSync } from "lucide-react";
import { SignUpDataTable } from "../components/signUp/SignUpDataTable";
import { SignUpSheet } from "../components/signUp/SignUpSheet";
import { EditSignUpSheet } from "../components/signUp/EditSignUpSheet";
import { RemoveSignUpDialog } from "../components/signUp/RemoveSignUpDialog";

const StudentSignUp = () => {
  const [open, setOpen] = useState<{
    create: {
      open: boolean;
      data?: { rotation: any; session: any };
    };
    edit: {
      open: boolean;
      data?: {
        rotation: any;
        session: any;
        enrollment?: any;
        preferences?: any;
      };
    };
    remove: {
      open: boolean;
      data?: {
        rotation: any;
        session: any;
        enrollment?: any;
        preferences?: any;
      };
    };
  }>({
    create: {
      open: false,
      data: undefined,
    },
    edit: {
      open: false,
      data: undefined,
    },
    remove: {
      open: false,
      data: undefined,
    },
  });
  const [rotations, setRotations] = useState<{
    rotations: any[];
    sessions: any[];
    enrollments: any[];
    preferences: any[];
  }>({ rotations: [], sessions: [], enrollments: [], preferences: [] });

  const { loadRotations, loading } = useSignUp();

  useEffect(() => {
    loadRotations(setRotations);
  }, []);

  const onCreate = (rotationId: string, sessionId: string) => {
    const rotation = rotations.rotations.find((r) => r._id === rotationId);
    const session = rotations.sessions.find((s) => s._id === sessionId);
    setOpen((prev) => ({
      ...prev,
      create: { open: true, data: { rotation, session } },
    }));
  };

  const onEditSingle = (
    rotationId: string,
    sessionId: string,
    enrollmentId: string,
  ) => {
    const rotation = rotations.rotations.find((r) => r._id === rotationId);
    const session = rotations.sessions.find((s) => s._id === sessionId);
    const enrollment = rotations.enrollments.find(
      (e) => e._id === enrollmentId,
    );
    setOpen((prev) => ({
      ...prev,
      edit: { open: true, data: { rotation, session, enrollment } },
    }));
  };

  const onEditMulti = (
    rotationId: string,
    sessionId: string,
    preferencesId: string,
  ) => {
    const rotation = rotations.rotations.find((r) => r._id === rotationId);
    const session = rotations.sessions.find((s) => s._id === sessionId);
    const preferences = rotations.preferences.find(
      (p) => p._id === preferencesId,
    );
    setOpen((prev) => ({
      ...prev,
      edit: { open: true, data: { rotation, session, preferences } },
    }));
  };

  const onRemoveSingle = (
    rotationId: string,
    sessionId: string,
    enrollmentId: string,
  ) => {
    const rotation = rotations.rotations.find((r) => r._id === rotationId);
    const session = rotations.sessions.find((s) => s._id === sessionId);
    const enrollment = rotations.enrollments.find(
      (e) => e._id === enrollmentId,
    );
    setOpen((prev) => ({
      ...prev,
      remove: { open: true, data: { rotation, session, enrollment } },
    }));
  };

  const onRemoveMulti = (
    rotationId: string,
    sessionId: string,
    preferencesId: string,
  ) => {
    const rotation = rotations.rotations.find((r) => r._id === rotationId);
    const session = rotations.sessions.find((s) => s._id === sessionId);
    const preferences = rotations.preferences.find(
      (p) => p._id === preferencesId,
    );
    setOpen((prev) => ({
      ...prev,
      remove: { open: true, data: { rotation, session, preferences } },
    }));
  };

  return (
    <Dashboard activePage="/rotations">
      <VStack>
        <h2
          style={{
            fontSize: "var(--text-2xl)",
            lineHeight: "var(--line-height-2xl)",
            fontWeight: "var(--font-bold)",
          }}
        >
          Sign Up
        </h2>
        <p style={{ color: "var(--muted-foreground)" }}>
          View and sign-up for explorations in available sessions.
        </p>
      </VStack>
      {loading.loadRotations ? (
        <Skeleton style={{ width: "100%", height: "512px" }} />
      ) : rotations.sessions.length < 1 ? (
        <Empty style={{ borderColor: "var(--secondary-border)" }}>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <CalendarSync />
            </EmptyMedia>
            <EmptyTitle>No Sessions Found</EmptyTitle>
            <EmptyDescription>
              No sessions open for sign-up found. If you believe this is an
              error please refresh.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <SignUpDataTable
          data={rotations}
          onCreate={onCreate}
          onEditSingle={onEditSingle}
          onEditMulti={onEditMulti}
          onRemoveSingle={onRemoveSingle}
          onRemoveMulti={onRemoveMulti}
        />
      )}
      {open.create.data?.rotation && open.create.data.session && (
        <SignUpSheet
          open={open.create.open}
          onOpenChange={(open) => {
            setOpen((prev) => ({
              ...prev,
              create: { open, data: prev.create.data },
            }));
          }}
          data={open.create.data}
          setRotations={setRotations}
        />
      )}
      {open.edit.data?.rotation &&
        open.edit.data.session &&
        (open.edit.data.enrollment || open.edit.data.preferences) && (
          <EditSignUpSheet
            open={open.edit.open}
            onOpenChange={(open) => {
              setOpen((prev) => ({
                ...prev,
                edit: { open, data: prev.edit.data },
              }));
            }}
            data={open.edit.data}
            setRotations={setRotations}
          />
        )}
      {open.remove.data?.rotation &&
        open.remove.data.session &&
        (open.remove.data.enrollment || open.remove.data.preferences) && (
          <RemoveSignUpDialog
            open={open.remove.open}
            onOpenChange={(open) => {
              setOpen((prev) => ({
                ...prev,
                remove: { open, data: prev.remove.data },
              }));
            }}
            data={open.remove.data}
            setRotations={setRotations}
          />
        )}
    </Dashboard>
  );
};

export default StudentSignUp;
